import React, { useEffect, useMemo, useState } from 'react';
import { collection, deleteDoc, doc, getDoc, onSnapshot, orderBy, query, updateDoc } from 'firebase/firestore';
import { FiCheckCircle, FiClock, FiPhone, FiUser, FiHash } from 'react-icons/fi';
import toast, { Toaster } from 'react-hot-toast';
import { db } from '../protected/firebase';
import { useAuth } from '../../../contexts/AuthContext';

export default function PickupDeliveryStatus() {
  const { companyId } = useAuth();
  const [orders, setOrders] = useState([]);
  const [fulfillmentType, setFulfillmentType] = useState('pickup');
  const [publicId, setPublicId] = useState(null);

  useEffect(() => {
    if (!companyId) return;
    getDoc(doc(db, `companies/${companyId}`)).then((snap) => {
      if (snap.exists()) {
        setPublicId(snap.data()?.publicId || null);
      }
    });
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return undefined;
    const ref = query(collection(db, `companies/${companyId}/preorders`), orderBy('timestamp', 'asc'));
    const unsub = onSnapshot(ref, (snapshot) => {
      const normalized = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        const timestamp = (data.timestamp && typeof data.timestamp.toDate === 'function')
          ? data.timestamp.toDate()
          : (data.timestamp ? new Date(data.timestamp) : new Date());
        return { id: docSnap.id, ...data, timestamp };
      });

      const completed = normalized.filter((order) => {
        const status = (order.orderStatus || '').toLowerCase();
        return status === 'completed' || status === 'ready';
      });

      completed.sort((a, b) => a.timestamp - b.timestamp);
      setOrders(completed);
    });

    return () => unsub();
  }, [companyId]);

  const normalizePickupStatus = (value) => {
    const raw = String(value || '').trim().toLowerCase();
    if (raw === 'picked up' || raw === 'picked-up') return 'picked_up';
    if (raw === 'not picked up' || raw === 'not-picked-up') return 'not_picked_up';
    return raw || 'not_picked_up';
  };

  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      const orderType = (order.orderType || order.customerInfo?.orderType || '').toLowerCase();
      const isDelivery = orderType === 'delivery';
      return fulfillmentType === 'delivery' ? isDelivery : !isDelivery;
    });
  }, [orders, fulfillmentType]);

  const handlePickupStatusChange = async (orderId, status) => {
    if (!companyId) return;
    try {
      const ref = doc(db, `companies/${companyId}/preorders/${orderId}`);
      await updateDoc(ref, {
        pickupStatus: status,
        pickedUpAt: status === 'picked_up' ? new Date().toISOString() : null,
        updatedAt: new Date().toISOString(),
      });
    } catch (err) {
      console.error('Failed to update pickup status', err);
      toast.error('Unable to update pickup status.');
    }
  };

  const handleCompletePickup = async (order) => {
    if (!companyId) return;
    const pickupStatus = normalizePickupStatus(order.pickupStatus);
    if (pickupStatus !== 'picked_up') {
      toast.error('Mark as picked up before completing.');
      return;
    }
    try {
      const ref = doc(db, `companies/${companyId}/preorders/${order.id}`);
      await deleteDoc(ref);

      if (publicId) {
        const publicRef = doc(db, `publicUsers/${publicId}/receipts/${order.id}`);
        const publicSnap = await getDoc(publicRef);
        if (publicSnap.exists()) {
          await deleteDoc(publicRef);
        }
      }
      const phoneRaw = order.customerInfo?.phone || order.customerPhone || '';
      const phoneKey = phoneRaw.replace(/\s+/g, '');
      if (phoneKey) {
        const onlineRef = doc(db, `companies/${companyId}/onlineOrders/${phoneKey}/data/${order.id}`);
        await deleteDoc(onlineRef);
      }
      toast.success('Pickup completed.');
    } catch (err) {
      console.error('Failed to complete pickup status', err);
      toast.error('Unable to complete pickup status.');
    }
  };

  return (
    <div className="p-4 md:p-6 min-h-screen">
      <Toaster position="top-right" toastOptions={{
        style: {
          background: '#1f2937',
          color: '#fff',
          border: '1px solid #374151',
          fontSize: '1.1rem',
          padding: '1rem'
        }
      }} />

      <div className="mx-auto">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-6">
          <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-purple-400 to-purple-600 bg-clip-text text-transparent">
            PICKUP &amp; DELIVERY STATUS
          </h2>
          <div className="flex items-center gap-3">
            <label className="text-sm font-semibold text-gray-300">Status View</label>
            <select
              value={fulfillmentType}
              onChange={(e) => setFulfillmentType(e.target.value)}
              className="bg-gray-800 border border-gray-700 text-gray-100 rounded-lg px-3 py-2 text-sm font-semibold"
            >
              <option value="pickup">Pickup</option>
              <option value="delivery">Delivery</option>
            </select>
          </div>
        </div>

        <div className="bg-gray-800 rounded-xl p-5 border-2 border-gray-700 shadow-xl">
          <div className="flex items-center mb-4">
            <FiClock className="text-2xl text-yellow-400 mr-3" />
            <h3 className="text-2xl font-bold text-gray-100">
              {fulfillmentType === 'delivery' ? 'DELIVERY STATUS' : 'PICKUP STATUS'}
            </h3>
          </div>

          {filteredOrders.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              <FiClock className="mx-auto text-5xl mb-4 opacity-30" />
              <p className="text-xl">
                {fulfillmentType === 'delivery'
                  ? 'No delivery orders ready'
                  : 'No pickup orders ready'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredOrders.map((order) => {
                const pickupStatus = normalizePickupStatus(order.pickupStatus);
                const paymentStatus = (order.paymentStatus || 'unpaid').toLowerCase();
                const pickupDate = order.pickupDate || order.customerInfo?.pickupDate;
                const pickupTime = order.pickupTime || order.customerInfo?.pickupTime;
                const pickupOption = order.pickupOption || order.customerInfo?.pickupOption;
                const canComplete = pickupStatus === 'picked_up';
                return (
                  <div
                    key={order.id}
                    className="bg-gray-750 border-l-4 border-yellow-500 rounded-r-xl overflow-hidden"
                  >
                    <div className="p-5">
                      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                        <div>
                          <div className="flex items-center flex-wrap gap-2">
                            <FiHash className="text-yellow-400 text-xl" />
                            <span className="font-bold text-2xl text-yellow-300">
                              {String(order.queueNumber).padStart(3, '0')}
                            </span>
                            <span
                              className={`text-sm px-3 py-1 rounded-full font-semibold ${
                                pickupStatus === 'picked_up'
                                  ? 'bg-emerald-600/90 text-white'
                                  : 'bg-orange-500/90 text-white'
                              }`}
                            >
                              {pickupStatus === 'picked_up' ? 'PICKED UP' : 'NOT PICKED UP'}
                            </span>
                            <span
                              className={`text-sm px-3 py-1 rounded-full font-semibold ${
                                paymentStatus === 'paid'
                                  ? 'bg-emerald-600/90 text-white'
                                  : 'bg-red-600/90 text-white'
                              }`}
                            >
                              {paymentStatus.toUpperCase()}
                            </span>
                          </div>
                          {(order.customerName || order.customerInfo?.name) && (
                            <div className="flex items-center mt-2 text-lg text-gray-300">
                              <FiUser className="mr-2 text-base" />
                              {order.customerName || order.customerInfo?.name}
                            </div>
                          )}
                          {(order.customerPhone || order.customerInfo?.phone) && (
                            <div className="flex items-center mt-1 text-base text-gray-400">
                              <FiPhone className="mr-2 text-sm" />
                              {order.customerPhone || order.customerInfo?.phone}
                            </div>
                          )}
                          {(pickupOption === 'now' || pickupOption === 'preorder' || pickupDate || pickupTime) && (
                            <div className="flex items-center mt-1 text-base text-green-300">
                              <FiClock className="mr-2 text-sm" />
                              <span>
                                {pickupOption === 'now'
                                  ? 'Pickup: Now'
                                  : pickupOption === 'preorder'
                                  ? 'Pickup: Scheduled'
                                  : `Pickup: ${pickupDate || '—'}${pickupDate && pickupTime ? ' • ' : ' '}${pickupTime || ''}`}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                          <div className="flex items-center gap-2">
                            <label className="text-sm font-semibold text-gray-300">Pickup Status</label>
                            <select
                              value={pickupStatus}
                              onChange={(e) => handlePickupStatusChange(order.id, normalizePickupStatus(e.target.value))}
                              className="bg-gray-900 border border-gray-700 text-gray-100 rounded-lg px-3 py-2 text-sm font-semibold"
                            >
                              <option value="not_picked_up">Not picked up</option>
                              <option value="picked_up">Picked up</option>
                            </select>
                          </div>
                          <button
                            onClick={() => handleCompletePickup(order)}
                            disabled={!canComplete}
                            className="flex items-center gap-2 px-5 py-2 bg-green-600 hover:bg-green-500 text-white text-lg font-bold rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            <FiCheckCircle className="text-xl" />
                            COMPLETE
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
